<template>
    <div
        class="custom-page-block-builder"
        :class="{
            grid: gridSystemEnabled,
            'flex-layout': flexLayoutEnabled
        }"
    >
        <div
            v-for="block in gridBlocks({ onlyVisible: false })"
            :key="block.id"
            :class="{ 'grid-cell': gridSystemEnabled, 'h-full': !gridSystemEnabled }"
            :style="getGridCellStyles(block)"
        >
            <template v-if="getDisplayValue(block.id) && shouldBlockBeDisplayed(block)">
                <CustomPageBlock
                    :model-value="block.currentValue"
                    :block="block"
                    :block-hoisted="hoisted"
                    :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                    @update:block="handleBlockUpdate(block.id, $event)"
                    @download-document="handleDocumentDownload"
                />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, provide } from 'vue'
import components, { type CPInputConfiguration, type DisplayDocumentPayload, type ExtendedBlock, type ListView, getCustomPageBlockComponent } from 'widget-components'
import { path, sum } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import useApi from '../api'
import { kebabCase } from 'change-case'
import { checkIfComponentIsFormComponent, isGridLayoutProvided } from '../functions'
import { useBlockDecorators, TooltipBlockDecorator } from 'widget-components/integrations/CustomPage'
import { useLazyBlockTools } from '../composables/useLazyBlockTools'
import { useEventBus } from '@vueuse/core'
import type { GetConditionValueInject } from 'widget-components/composables/conditions/useConditions'

useBlockDecorators(TooltipBlockDecorator)
useLazyBlockTools()

const { CustomPageBlock } = components

const props = defineProps<{
    blocks: ExtendedBlock[]
    hoisted?: boolean
    loading: boolean
    customPageId?: string
    conditionsInitialized: boolean
}>()

const getDisplayValue = inject<GetConditionValueInject>('getDisplayValue', (_: string) => true)

const { downloadDocument } = useApi()
const isLoading = computed(() => props.loading || !props.conditionsInitialized)

const gridSystemEnabled = computed(() => isGridLayoutProvided(props.blocks))
const flexLayoutEnabled = computed(() => props.blocks[0]?.type === 'CONTAINER')

const gridBlocks = computed(() => (opts: { onlyVisible: boolean }) =>
    (props.blocks || [])
        .filter(b => opts.onlyVisible ? getDisplayValue(b.id) : true)
        .sort((a, b) =>
            sum([(a.layout?.y || 0) * 1000 || 0, a.layout?.x || 0]) <
            sum([(b.layout?.y || 0) * 1000 || 0, b.layout?.x || 0]) ? -1 : 1,
        ),
)

const handleDocumentDownload = async (resource: DisplayDocumentPayload) => {
    const response = await downloadDocument({
        componentId: resource.componentId,
        resourceId: resource.resourceId,
        resourceFileName: resource.resourceName,
    })

    if (response.data) {
        const blobFile = response.data
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blobFile)
        a.target = '_blank'
        a.click()
    }
}

const emit = defineEmits<{
    (e: 'update-block', id: string, block: ExtendedBlock): void
    (e: 'render:error', err: unknown): void
}>()

const handleBlockUpdate = (id: string, block: ExtendedBlock) => {
    emit('update-block', id, block)
}

// DEPRECATED - used only in CP1
const resetModelValue = (id: string) => {
    const component = clone(props.blocks.find(c => c.id === id))
    if (component && checkIfComponentIsFormComponent(component)) {
        if (component.type === 'LIST_VIEW') {
            (component as ExtendedBlock<ListView>).items.forEach((item, containerItemIndex) => {
                item.components.forEach((c, componentIndex) => {
                    c.initialValue = clone(component.currentValue?.[containerItemIndex]?.[componentIndex])
                    c.currentValue = ''
                })
            })
        }
        component.initialValue = clone(component.currentValue)
        component.currentValue = ''

        emit('update-block', id, component)
    }
}

useEventBus<string>('block-unmounted-event').on(resetModelValue)

const shouldBlockBeDisplayed = (block: ExtendedBlock) => {
    if (!gridSystemEnabled.value) {
        return true
    }
    // EMPTY_SPACE in grid is expanding grid-cell, which should not take place for full compatibility with gridstack
    return block.type !== 'EMPTY_SPACE'
}

const DCH = 6 // Default Component Height = 60px like input or button
const CELL_HEIGHT = 10 // px, ex. 16 means 160px

const EMPTY_SPACE_HEIGHT = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
}

const getBlockHeight = (block: ExtendedBlock) => {
    const h = block.layout?.h as number

    if (block.type === 'EMPTY_SPACE') {
        return h || EMPTY_SPACE_HEIGHT[
                path(['configuration', 'size'], block) as keyof typeof EMPTY_SPACE_HEIGHT
        ] || DCH
    }

    return h || DCH
}

const computeYPositions = (blocks: ExtendedBlock[]) => {
    const positions: Record<string, number> = {}
    const columns: Record<number, number> = {}

    blocks.forEach((block) => {
        const x = block.layout?.x || 0
        const w = block.layout?.w || 1
        const h = block.layout?.h || 1

        // Find the highest possible Y item for the block
        let y = 0
        for (let col = x; col < x + w; col++) {
            if (columns[col] !== undefined) {
                y = Math.max(y, columns[col])
            }
        }

        positions[block.id || ''] = y

        // Update the height of the columns
        for (let col = x; col < x + w; col++) {
            columns[col] = y + h
        }
    })

    return positions
}

const getGridCellStyles = (block: ExtendedBlock) => {
    if (!gridSystemEnabled.value) {
        return { }
    }

    const x = block.layout?.x || 0
    const w = block.layout?.w || 1
    const h = getBlockHeight(block)

    const visibleBlocks = gridBlocks.value({ onlyVisible: true })
    const yPositions = computeYPositions(visibleBlocks as ExtendedBlock[])

    const y = yPositions[block.id || '']

    if (y === undefined) return { display: 'none' }

    return {
        ...block.type === 'DIVIDER' ? {
            '--divider-height': `${CELL_HEIGHT * getBlockHeight(block)}px`,
        } : {},
        ...block.type === 'EMPTY_SPACE' ? {
            height: `${h * 10}px`, overflow: 'hidden',
        } : {},
        gridColumn: `${x + 1} / span ${w}`,
        gridRow: `${y + 1} / span ${h}`,
    }
}


provide('getCustomPageBlockComponent', getCustomPageBlockComponent)
provide('handleDocumentDownload', handleDocumentDownload)
provide('isLoading', isLoading)
</script>

<style scoped lang="scss">
.custom-page-block-builder {
    &.grid {
        height: auto;
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        grid-auto-rows: minmax(0, auto);
        column-gap: 8px;

        > * {
            padding-bottom: 4px;
        }
    }

    > * {
        --tl-cp-block-margin: 0;
    }

    &.flex-layout {
        height: 100%;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
